import { Survey } from "@/features/surveys/survey-types";

const faceCreamSurvey: Survey = {
  id: "face-cream-market-research",
  metadata: {
    createdBy: "Marketing Manager",
    creationDate: "2024-12-24",
    language: "English",
    version: "1.0",
  },
  header: {
    title: "Market Research for a New Face Cream",
    contextAndObjectives:
      "Evaluate the potential market reception of a new face cream targeting women aged 30-40 in the US.",
  },
  demographicTarget: {
    targetAudience:
      "Women aged 30-40, living in medium and large cities in the US, belonging to the middle and upper class.",
  },
  questions: [
    // Skincare Habits and Preferences
    {
      id: "q4",
      text: "How often do you use face creams?",
      type: "multiple-choice",
      choices: [
        { text: "Daily" },
        { text: "Several times a week" },
        { text: "Rarely" },
        { text: "Never" },
      ],
    },
    {
      id: "q5",
      text: "What are the top three factors you consider when purchasing a face cream?",
      type: "multiple-choice",
      choices: [
        { text: "The brand reputation" },
        { text: "The ingredients" },
        { text: "The price" },
        { text: "The packaging" },
        { text: "Effectiveness backed by science" },
      ],
      multipleSelection: true,
    },
    {
      id: "q6",
      text: "Are you currently using any anti-wrinkle or preventive skincare products? If yes, which ones?",
      type: "text",
    },
    // Perception of Product Value and Pricing
    {
      id: "q7",
      text: "What is your typical budget for a 50 ml face cream?",
      type: "number",
      min: 0,
      max: 500,
    },
    {
      id: "q8",
      text: "Would you consider paying $80 for a face cream that prevents future wrinkles? Why or why not?",
      type: "text",
    },
    // Product Messaging and Appeal
    {
      id: "q9",
      text: "How important is it for you to prevent wrinkles in the future?",
      type: "opinion-scale",
      min: 1,
      max: 5,
      minLabel: "Not Important",
      maxLabel: "Very Important",
    },
    {
      id: "q10",
      text: "How would you describe your skincare priorities?",
      type: "multiple-choice",
      choices: [
        { text: "Acne and breakouts" },
        { text: "Dryness and dehydration" },
        { text: "Oily skin and enlarged pores" },
        { text: "Fine lines and wrinkles" },
        { text: "Hyperpigmentation and dark spots" },
        { text: "Redness and sensitivity" },
        { text: "Uneven skin tone and texture" },
        { text: "Dark circles and puffiness under the eyes" },
        { text: "Sun damage and UV protection" },
        { text: "Loss of elasticity and firmness" },
      ],
      multipleSelection: true,
    },
    // Brand and Advertising Insights
    {
      id: "q11",
      text: "Which skincare brands do you trust most and why?",
      type: "text",
    },
    {
      id: "q12",
      text: "What type of advertising appeals to you when it comes to beauty products?",
      type: "text",
    },
  ],
};

const remoteWorkSurvey: Survey = {
  id: "remote-work-optimization",
  metadata: {
    createdBy: "HR Director",
    creationDate: "2024-12-24",
    language: "English",
    version: "1.0",
  },
  header: {
    title: "Remote Work Optimization Survey",
    contextAndObjectives:
      "Gather insights from employees to improve remote work policies, enhance productivity, and support work-life balance.",
  },
  demographicTarget: {
    targetAudience:
      "Employees in the tech industry working remotely for at least six months.",
  },
  questions: [
    // Work Environment
    {
      id: "q1",
      text: "How many days per week do you typically work remotely?",
      type: "multiple-choice",
      choices: [
        { text: "1 day" },
        { text: "2-3 days" },
        { text: "4-5 days" },
        { text: "Always remote" },
      ],
    },
    {
      id: "q2",
      text: "How would you rate the adequacy of your current home office setup?",
      type: "opinion-scale",
      min: 1,
      max: 5,
      minLabel: "Inadequate",
      maxLabel: "Excellent",
    },
    {
      id: "q3",
      text: "What are the main challenges you face while working remotely? (e.g., distractions, lack of equipment, poor internet connectivity)",
      type: "text",
    },
    // Productivity and Collaboration
    {
      id: "q4",
      text: "How does remote work impact your productivity compared to working in an office?",
      type: "multiple-choice",
      choices: [
        { text: "Significantly more productive" },
        { text: "Slightly more productive" },
        { text: "No difference" },
        { text: "Slightly less productive" },
        { text: "Significantly less productive" },
      ],
    },
    {
      id: "q5",
      text: "How do you typically communicate with your team while working remotely?",
      type: "ranking",
      choices: [
        { text: "Video calls" },
        { text: "Chat applications (e.g., Slack, Teams)" },
        { text: "Emails" },
        { text: "Phone calls" },
        { text: "Project management tools (e.g., Trello, Asana)" },
      ],
    },
    {
      id: "q6",
      text: "Do you feel adequately supported by your company when working remotely? If not, what improvements would you suggest?",
      type: "text",
    },
    // Work-Life Balance
    {
      id: "q7",
      text: "How has remote work affected your work-life balance?",
      type: "opinion-scale",
      min: 1,
      max: 5,
      minLabel: "Worsened significantly",
      maxLabel: "Improved significantly",
    },
    {
      id: "q8",
      text: "What measures or policies could improve your work-life balance while working remotely?",
      type: "text",
    },
    // Future Preferences
    {
      id: "q9",
      text: "What is your ideal remote work arrangement?",
      type: "multiple-choice",
      choices: [
        { text: "Fully remote" },
        { text: "Hybrid (1-2 days in office)" },
        { text: "Hybrid (3-4 days in office)" },
        { text: "Fully in-office" },
      ],
    },
    {
      id: "q10",
      text: "Would you recommend remote work as a permanent option to other employees? Why or why not?",
      type: "text",
    },
  ],
};

const evAdoptionSurvey: Survey = {
  id: "ev-adoption-survey",
  metadata: {
    createdBy: "Product Strategist",
    creationDate: "2024-12-24",
    language: "English",
    version: "1.0",
  },
  header: {
    title: "Consumer Attitudes Toward Electric Vehicles",
    contextAndObjectives:
      "Assess public perceptions, preferences, and concerns to better understand what influences EV purchasing decisions.",
  },
  demographicTarget: {
    targetAudience:
      "Adults aged 25-60 with a valid driver's license, considering purchasing a vehicle in the next 3 years.",
  },
  questions: [
    // Vehicle Usage and Lifestyle
    {
      id: "q1",
      text: "How many miles do you typically drive per week?",
      type: "number",
      min: 0,
      max: 1000,
    },
    {
      id: "q2",
      text: "What is the primary purpose of your vehicle?",
      type: "multiple-choice",
      choices: [
        { text: "Commuting to work" },
        { text: "Leisure and personal errands" },
        { text: "Family transportation" },
        { text: "Business use" },
        { text: "Other" },
      ],
    },
    {
      id: "q3",
      text: "What type of vehicle do you currently own or use most often?",
      type: "multiple-choice",
      choices: [
        { text: "Gasoline-powered" },
        { text: "Hybrid (gasoline + electric)" },
        { text: "Electric (fully electric)" },
        { text: "None (I don’t own a vehicle)" },
      ],
    },
    // Attitudes Toward EVs
    {
      id: "q4",
      text: "How familiar are you with electric vehicles and their benefits?",
      type: "opinion-scale",
      min: 1,
      max: 5,
      minLabel: "Not Familiar at All",
      maxLabel: "Very Familiar",
    },
    {
      id: "q5",
      text: "What factors would motivate you to purchase an electric vehicle?",
      type: "ranking",
      choices: [
        { text: "Lower cost of ownership (e.g., fuel, maintenance)" },
        { text: "Environmental benefits (e.g., reduced emissions)" },
        { text: "Access to incentives (e.g., tax credits)" },
        { text: "Advanced technology and features" },
        { text: "Performance (e.g., acceleration, quiet drive)" },
      ],
    },
    {
      id: "q6",
      text: "What concerns do you have about electric vehicles?",
      type: "text",
    },
    // Barriers to Adoption
    {
      id: "q7",
      text: "How important is access to public charging stations when considering an electric vehicle?",
      type: "opinion-scale",
      min: 1,
      max: 5,
      minLabel: "Not Important",
      maxLabel: "Very Important",
    },
    {
      id: "q8",
      text: "What is the minimum driving range you expect from an electric vehicle on a full charge?",
      type: "number",
      min: 0,
      max: 1000,
    },
    {
      id: "q9",
      text: "What price range would you consider reasonable for an electric vehicle?",
      type: "text",
    },
    // EV Features and Preferences
    {
      id: "q10",
      text: "Which of the following features would be most appealing in an electric vehicle?",
      type: "ranking",
      choices: [
        { text: "Fast charging capability" },
        { text: "Long driving range" },
        { text: "Advanced driver-assistance systems" },
        { text: "Luxury interior design" },
        { text: "Customizable options" },
      ],
    },
    {
      id: "q11",
      text: "Which electric vehicle brands are you most familiar with?",
      type: "text",
    },
    {
      id: "q12",
      text: "What type of advertising or information would most influence your decision to purchase an electric vehicle?",
      type: "text",
    },
  ],
};

const streamingSurvey: Survey = {
  id: "streaming-preferences-survey",
  metadata: {
    createdBy: "Marketing Strategist",
    creationDate: "2024-12-24",
    language: "English",
    version: "1.0",
  },
  header: {
    title: "Consumer Preferences for Streaming Services",
    contextAndObjectives:
      "Understand consumer habits, content preferences, and willingness to pay for streaming services to shape a competitive offering.",
  },
  demographicTarget: {
    targetAudience:
      "Adults aged 18-55 with internet access, who actively watch streaming content at least once a week.",
  },
  questions: [
    // Viewing Habits
    {
      id: "q1",
      text: "How many hours per week do you spend watching streaming services?",
      type: "number",
      min: 0,
      max: 100,
    },
    {
      id: "q2",
      text: "Which devices do you primarily use for streaming content?",
      type: "multiple-choice",
      choices: [
        { text: "Smart TV" },
        { text: "Laptop/Desktop" },
        { text: "Tablet" },
        { text: "Smartphone" },
        { text: "Streaming device (e.g., Roku, Fire Stick)" },
      ],
    },
    {
      id: "q3",
      text: "What time of day do you usually watch streaming content?",
      type: "multiple-choice",
      choices: [
        { text: "Morning" },
        { text: "Afternoon" },
        { text: "Evening" },
        { text: "Late Night" },
      ],
    },
    // Content Preferences
    {
      id: "q4",
      text: "What type of content do you enjoy most on streaming services?",
      type: "ranking",
      choices: [
        { text: "Movies" },
        { text: "TV Series" },
        { text: "Documentaries" },
        { text: "Reality Shows" },
        { text: "Live Sports" },
        { text: "Kids/Family Content" },
      ],
    },
    {
      id: "q5",
      text: "What are your favorite genres of streaming content?",
      type: "text",
    },
    {
      id: "q6",
      text: "How important is it for a streaming service to offer exclusive content (e.g., original movies or series)?",
      type: "opinion-scale",
      min: 1,
      max: 5,
      minLabel: "Not Important",
      maxLabel: "Very Important",
    },
    // Subscription Preferences
    {
      id: "q7",
      text: "How much are you willing to pay monthly for a streaming service?",
      type: "number",
      min: 0,
      max: 100,
    },
    {
      id: "q8",
      text: "What factors are most important when choosing a streaming service?",
      type: "ranking",
      choices: [
        { text: "Price" },
        { text: "Content library size" },
        { text: "Exclusive content" },
        { text: "Ad-free experience" },
        { text: "Multiple user profiles" },
      ],
    },
    {
      id: "q9",
      text: "Would you consider a service that includes ads for a lower subscription fee?",
      type: "yes-no",
    },
    // Competitive Insights
    {
      id: "q10",
      text: "Which streaming services do you currently use and why?",
      type: "text",
    },
    {
      id: "q11",
      text: "What do you dislike about your current streaming service(s)?",
      type: "text",
    },
    {
      id: "q12",
      text: "What features would make you switch to a new streaming platform?",
      type: "text",
    },
  ],
};

export const sampleSurveys: Survey[] = [
  faceCreamSurvey,
  remoteWorkSurvey,
  evAdoptionSurvey,
  streamingSurvey,
];
